import { useState, useEffect, useRef } from "react";
import { Check, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { mskTodayStr } from "../lib/mskDate.js";
import { validatePatientReportDetails } from "../lib/patientReportDetails.js";
import { copyText } from "../lib/clipboard.js";

export default function PatientReportDetailsModal({ patient, onSave, onCancel }) {
  const [alias, setAlias] = useState(patient.alias || "");
  const [birthDate, setBirthDate] = useState(patient.birthDate || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [copyHint, setCopyHint] = useState("");
  const dialogRef = useRef(null);

  useEffect(() => {
    const trapFocus = event => {
      if (event.key === "Escape") { event.preventDefault(); if (!saving) onCancel(); return; }
      if (event.key !== "Tab") return;
      const controls = [...dialogRef.current.querySelectorAll('button:not(:disabled), input:not(:disabled)')];
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && (document.activeElement === first || !dialogRef.current.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !dialogRef.current.contains(document.activeElement))) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener("keydown", trapFocus, true);
    return () => document.removeEventListener("keydown", trapFocus, true);
  }, [saving, onCancel]);

  const copyCardName = async () => {
    if (await copyText(patient.name || "")) {
      setCopyHint("");
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } else {
      setCopyHint("Выделите имя из карточки и скопируйте его вручную.");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const result = validatePatientReportDetails({ alias, birthDate }, mskTodayStr());
    if (result.error) { setError(result.error); return; }
    setSaving(true);
    setError("");
    try {
      await onSave(result.data);
    } catch (err) {
      setError(err.response?.data?.error || "Не удалось сохранить данные пациента");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/55 z-[260] flex items-center justify-center p-4">
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="patient-report-details-title" className="bg-white rounded-xl shadow-2xl w-[400px] max-w-[95vw] max-h-[90vh] overflow-y-auto">
        <div className="px-5 pt-5 pb-3">
          <h2 id="patient-report-details-title" className="text-sm font-semibold text-slate-900">Данные для отчёта</h2>
          <p className="text-xs text-slate-500 mt-1">Укажите ФИО и дату рождения пациента.</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-5 pb-4 flex flex-col gap-3">
            <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2">
              <div className="min-w-0 flex-1">
                <span className="text-[11px] text-slate-500">В карточке</span>
                <p className="text-sm text-slate-800 select-text break-words">{patient.name || "—"}</p>
              </div>
                <Button type="button" variant="ghost" size="icon" title="Копировать имя" aria-label="Копировать имя" className="h-7 w-7 shrink-0" onClick={copyCardName} disabled={saving}>
                  {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
            </div>
            {copyHint && <p role="status" className="text-xs text-slate-500">{copyHint}</p>}

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="report-patient-alias" className="text-xs text-slate-700">ФИО в отчёте (псевдоним)</Label>
              <Input
                id="report-patient-alias"
                autoFocus
                value={alias}
                onChange={e => setAlias(e.target.value)}
                placeholder="Введите ФИО, которое попадёт в отчёт"
                disabled={saving}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="report-patient-birth-date" className="text-xs text-slate-700">Дата рождения</Label>
              <Input
                id="report-patient-birth-date"
                type="date"
                max={mskTodayStr()}
                value={birthDate}
                onChange={e => setBirthDate(e.target.value)}
                disabled={saving}
              />
            </div>

            {error && <div role="alert" className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 border-t border-slate-100">
            <Button type="button" size="sm" variant="ghost" disabled={saving} onClick={onCancel}>Другой пациент</Button>
            <Button type="submit" size="sm" disabled={saving}>
              {saving ? "Сохранение..." : "Продолжить"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
